// One engineer's editable weekly timesheet (Session AEL, Director decisions D41 + D31):
// /office/timesheets/:engineerId/:weekStarting. This is the office-signed-off record (travel,
// on-site, office and training time), distinct from the read-only derived roll-up on the
// TimesheetsPage overview. See that file's header comment for the two-figures rule.
//
// SESSION AUV, design system Session 8 (People): RecordHeader (the registry's `timesheet` pill beside the title, so the
// two CHANGE cells land here too: DRAFT purple, SUBMITTED amber), Loader, the primary buttons, and lib/format dates (a
// date prints "6 Jul 2026" where the heading and the delete confirm printed "6 July 2026", the day headings read
// "Monday 6 July 2026", and an approval date reads "6 Jul 2026"). THE PAY PANEL AND ITS ADMIN-ONLY GATE ARE UNTOUCHED
// (D57 open). Presentation only.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type {
  SearchResultItem,
  TimesheetEntry,
  TimesheetEntryInput,
  TimesheetEntryPatch,
  TimesheetEntryType,
  TimesheetPayReport,
  TimesheetSheet,
  TimesheetStatus,
  TimesheetUnrecordedJob,
} from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { jobTypeLabel } from "../../lib/jobType";
import { TimesheetBreakdown } from "../../components/TimesheetBreakdown";
import { TimesheetPayPanel } from "../../components/TimesheetPayPanel";
import { TimesheetStatementPanel } from "../../components/TimesheetStatementPanel";
import { serviceRecordHref } from "../../lib/serviceRecordLink";
import { formatDurationHm } from "../../lib/timeOnSite";
import { ConfirmButton } from "../../components/ConfirmButton";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate, formatDateLong } from "../../lib/format";

const ENTRY_TYPE_OPTIONS: { value: TimesheetEntryType; label: string }[] = [
  { value: "TRAVEL", label: "Travel" },
  { value: "ON_SITE", label: "On site" },
  { value: "OFFICE", label: "Office" },
  { value: "TRAINING", label: "Training" },
  { value: "OTHER", label: "Other" },
];

function addDays(ymd: string, n: number): string {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + n);
  const pad = (x: number) => String(x).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

// A recorded ISO instant as a plain "HH:MM" in the office's own local time, for display and to
// seed the <input type="time"> when editing.
function toLocalTimeInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Combines a plain date with an "HH:MM" clock time, interpreted in the office's own local time,
// into the ISO-with-offset datetime string the server's entryCreateSchema/entryPatchSchema require.
function combineDateTime(dateYmd: string, hhmm: string): string {
  return new Date(`${dateYmd}T${hhmm}:00`).toISOString();
}

function entryTimeLabel(entry: TimesheetEntry): string {
  if (entry.startTime && entry.endTime) {
    return `${toLocalTimeInput(entry.startTime)} to ${toLocalTimeInput(entry.endTime)}`;
  }
  return formatDurationHm(entry.minutes);
}

// An "add" may carry a JOB to link the new entry to (Session ANF). That link is what lets the
// "Hours not on this timesheet" section below actually clear: the section lists a completed visit
// with no TimesheetEntry against it, so an entry typed with no jobId leaves the row standing for
// ever however many hours the office records. Nothing on the server changed for this --
// entryCreateSchema has always parsed jobId, the route has always validated and stored it, and
// TimesheetEntryInput has always declared it. No office UI had ever sent one.
type LinkedJob = { jobId: string; label: string };
type FormState = { mode: "add"; date: string; job?: LinkedJob } | { mode: "edit"; entry: TimesheetEntry };

export function TimesheetDetailPage() {
  const { engineerId, weekStarting } = useParams<{ engineerId: string; weekStarting: string }>();
  // Session ANH: the rate card check figure is ADMIN ONLY (the payroll CSV's access pattern, and
  // the server answers 403 to anyone else), so it is fetched and rendered only for an admin. The
  // standby toggle is an OFFICE decision and is offered to office and admin alike.
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sheet, setSheet] = useState<TimesheetSheet | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState | null>(null);
  const [pay, setPay] = useState<TimesheetPayReport | null>(null);
  const [payState, setPayState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  // The approve guard's refusal, held so the office can be shown WHY and then decide (Session ANF).
  // Never pre-armed and never inferred: the acknowledgement is only ever sent by a second, separate
  // press against a message naming the count, which is the shape update_contract's
  // acknowledgeSkippedCycle already uses for a deliberate override.
  const [pendingApproval, setPendingApproval] = useState<{ count: number; message: string } | null>(null);

  const reload = useCallback(async () => {
    if (!engineerId || !weekStarting) return;
    try {
      const res = await api.timesheets.sheet({ engineerId, weekStarting });
      setSheet(res);
      setState("ready");
    } catch (err) {
      setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
    }
    // The check figure is recomputed on every reload, since every edit above changes it. A failure
    // here never hides the timesheet, and never unmounts a figure already on screen (the panel may
    // be showing the result of the press that triggered the reload): it reports the failure and the
    // last good figure stays, marked as such.
    if (isAdmin) {
      setPayState("loading");
      try {
        setPay(await api.timesheets.pay({ engineerId, weekStarting }));
        setPayState("ready");
      } catch {
        setPayState("error");
      }
    }
  }, [engineerId, weekStarting, isAdmin]);

  // The standby-week toggle (Session ANH, rate card rule 10). The office's own decision, recorded
  // on the Timesheet row, so a week with no row yet is created first exactly as adding an entry
  // does. Refused by the server on an APPROVED week; the control is disabled to match.
  async function setStandby(next: boolean) {
    if (!engineerId || !weekStarting) return;
    setActionBusy(true);
    setActionError(null);
    try {
      let timesheetId = sheet?.timesheetId ?? null;
      if (!timesheetId) {
        const res = await api.timesheets.ensure(engineerId, weekStarting);
        timesheetId = res.timesheetId;
      }
      await api.timesheets.setStandby(timesheetId, next);
      await reload();
    } catch (err) {
      setActionError(err instanceof ApiError && err.message ? err.message : "Could not change the standby week. Please try again.");
    } finally {
      setActionBusy(false);
    }
  }

  useEffect(() => {
    setState("loading");
    void reload();
  }, [reload]);

  async function runStatusAction(fn: (id: string) => Promise<{ status: TimesheetStatus }>, fallbackMessage: string) {
    if (!sheet?.timesheetId) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await fn(sheet.timesheetId);
      await reload();
    } catch (err) {
      setActionError(err instanceof ApiError && err.message ? err.message : fallbackMessage);
    } finally {
      setActionBusy(false);
    }
  }

  // Approve, with the pending-visits guard in front of it (Session ANF). Pressed with
  // `acknowledged` false first, always: the server answers 409 jobs_awaiting_approval when visits
  // in this week are still unapproved, and its message (which names the count and says plainly that
  // the hours cannot be added afterwards without reopening) is shown so the office can decide. Only
  // a SECOND, deliberate press sends the acknowledgement. Nothing here ever sets it automatically,
  // and a failure for any other reason falls through to the ordinary error line.
  async function approve(acknowledged: boolean) {
    if (!sheet?.timesheetId) return;
    setActionBusy(true);
    setActionError(null);
    try {
      await api.timesheets.approve(sheet.timesheetId, acknowledged ? { acknowledgePendingJobs: true } : undefined);
      setPendingApproval(null);
      await reload();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.body?.error === "jobs_awaiting_approval") {
        const count = err.body.pendingCount;
        setPendingApproval({ count: typeof count === "number" ? count : 0, message: err.message });
      } else {
        setActionError(err instanceof ApiError && err.message ? err.message : `Could not approve ${sheet.engineerName}'s timesheet. Please try again.`);
      }
    } finally {
      setActionBusy(false);
    }
  }

  // A freshly-built entry never carries a null time (see EntryFormModal's header comment): nulls
  // there are only added when clearing an EXISTING pair on an edit, which never happens on the add
  // path. addEntry's own type (TimesheetEntryInput) has no null time members, so this cast is safe:
  // it is simply asserting a fact the type checker cannot see from the payload's shape alone. jobId
  // is the one field the modal itself may set to a real value or leave absent (Session ANX): the
  // picker below resolves it, this function only forwards whatever the modal decided.
  async function saveEntry(payload: TimesheetEntryPatch) {
    if (!engineerId || !weekStarting) return;
    let timesheetId = sheet?.timesheetId ?? null;
    if (!timesheetId) {
      const res = await api.timesheets.ensure(engineerId, weekStarting);
      timesheetId = res.timesheetId;
    }
    await api.timesheets.addEntry(timesheetId, payload as TimesheetEntryInput);
  }

  async function saveEditedEntry(entryId: string, payload: TimesheetEntryPatch) {
    if (!sheet?.timesheetId) throw new ApiError(409, "no_timesheet", undefined);
    await api.timesheets.updateEntry(sheet.timesheetId, entryId, payload);
  }

  const weekLabel = weekStarting ? `the week beginning ${formatDate(weekStarting)}` : "this week";

  return (
    <div>
      {state !== "ready" && (
        <Link to="/office/timesheets" className="font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green">
          &larr; Timesheets
        </Link>
      )}

      {state === "loading" ? (
        <Loader label="Loading the timesheet..." shape="panel" className="mt-4" />
      ) : state === "notfound" ? (
        <p role="alert" className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          That engineer could not be found.
        </p>
      ) : state === "error" ? (
        <p role="alert" className="mt-4 rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load this timesheet. Please try again in a moment.
        </p>
      ) : sheet ? (
        <div className="mt-3">
          <RecordHeader
            className="mb-4"
            title={`${sheet.engineerName}, week beginning ${formatDate(sheet.weekStarting)}`}
            backTo="/office/timesheets"
            backLabel="Timesheets"
            status={<StatusPill domain="timesheet" status={sheet.status} />}
            subtitle={
              sheet.status === "APPROVED"
                ? `Approved${sheet.approvedByName ? ` by ${sheet.approvedByName}` : ""}${sheet.approvedAt ? ` on ${formatDate(sheet.approvedAt)}` : ""}.`
                : undefined
            }
            actions={
              <>
                  {sheet.status === "DRAFT" && sheet.timesheetId && (
                    <ConfirmButton
                      onConfirm={() =>
                        void runStatusAction(api.timesheets.submit, `Could not submit ${sheet.engineerName}'s timesheet. Please try again.`)
                      }
                      busy={actionBusy}
                      busyLabel="Submitting..."
                      label="Submit"
                      confirmLabel={`Submit ${sheet.engineerName}'s timesheet for ${weekLabel}?`}
                      className="rounded-ele border border-ele-blue px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset"
                    />
                  )}
                  {sheet.status === "SUBMITTED" && (
                    <ConfirmButton
                      onConfirm={() => void approve(false)}
                      busy={actionBusy}
                      busyLabel="Approving..."
                      label="Approve"
                      confirmLabel={`Approve ${sheet.engineerName}'s timesheet for ${weekLabel}?`}
                      className="rounded-ele border border-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-green transition hover:bg-ele-inset"
                    />
                  )}
                  {sheet.status === "APPROVED" && (
                    <ConfirmButton
                      onConfirm={() =>
                        void runStatusAction(api.timesheets.reopen, `Could not reopen ${sheet.engineerName}'s timesheet. Please try again.`)
                      }
                      busy={actionBusy}
                      busyLabel="Reopening..."
                      label="Reopen"
                      confirmLabel={`Reopen ${sheet.engineerName}'s timesheet for ${weekLabel} for editing?`}
                      className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                    />
                  )}
              </>
            }
          />

          {actionError && (
            <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {actionError}
            </p>
          )}

          {pendingApproval && (
            <div role="alert" className="mb-4 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-3 text-sm text-ele-strong">
              <p>{pendingApproval.message}</p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <ConfirmButton
                  onConfirm={() => void approve(true)}
                  busy={actionBusy}
                  busyLabel="Approving..."
                  label="Approve anyway"
                  confirmLabel={`Approve this week with ${pendingApproval.count} visit${pendingApproval.count === 1 ? "" : "s"} still unapproved?`}
                  className="rounded-ele border border-ele-amber-line px-4 py-2 font-menu text-xs tracking-wide text-[var(--ele-amber-text-deep)] transition hover:bg-ele-inset"
                />
                <button
                  type="button"
                  onClick={() => setPendingApproval(null)}
                  className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-green"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!sheet.canEdit && (
            <p className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-body">
              This timesheet has been approved. Reopen it to make changes.
            </p>
          )}

          {/* Standby (Session ANH, rewritten by Session ANY). The ROTA is now the normal way a week
              is a standby week, so the box no longer stands alone as the only answer: the rota's
              verdict is stated first, in words, and the box is offered underneath as the exception
              it now is. The box still binds to standbyManual, the stored flag, NEVER to the
              effective value -- ticking it must record a decision, not silently re-record what the
              rota already said, and PATCH /:id/standby writes only that flag. */}
          <div className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3 text-sm">
            <p className="text-ele-strong">
              {sheet.standbyRota?.onStandby
                ? `The standby rota has ${sheet.engineerName} on standby this week.`
                : sheet.standbyRota?.engineerName
                  ? `The standby rota has ${sheet.standbyRota.engineerName} on standby this week, not ${sheet.engineerName}.`
                  : "The standby rota has nobody on standby this week."}
              {" "}
              <Link to="/office/standby" className="text-ele-green underline-offset-2 hover:underline">
                Standby rota
              </Link>
            </p>
            <div className="mt-3 flex items-start gap-3">
              <input
                id="ts-standby-week"
                type="checkbox"
                checked={sheet.standbyManual ?? false}
                disabled={!sheet.canEdit || actionBusy}
                onChange={(e) => void setStandby(e.target.checked)}
                className="mt-1 accent-ele-green"
                aria-describedby="ts-standby-week-help"
              />
              <span>
                <label htmlFor="ts-standby-week" className="text-ele-strong">
                  {sheet.engineerName} covered standby this week as well
                </label>
                <span id="ts-standby-week-help" className="block text-xs text-ele-body">
                  Only tick this for a week the rota does not cover, where {sheet.engineerName} covered standby anyway. Under the subcontractor rate card the standby
                  retainer is due for a week on standby or a week with a call-out attended; a week with neither carries no retainer.
                  {!sheet.canEdit ? " Reopen the week to change it." : ""}
                </span>
              </span>
            </div>
          </div>

          {/* Session ANN: the box is not this engineer's business at all when the rate card has
              nothing to say about them (pay.pay.rateCardEngineer null -- neither linked to a
              subcontractor record nor named by the card, e.g. a PAYE engineer like Adrian Baker or
              Darren). Not rendering it at all, rather than rendering it to say "not priced", is the
              fix: that message was correct and was noise on every such timesheet. Once we know
              `pay` (a successful load), applicability is decided by the server's own computation, so
              this never hardcodes a name here. Before we know it (payState "error" with no `pay` yet
              at all), the existing "could not be loaded" notice still shows, honestly, since we
              cannot yet say the box does not apply. */}
          {isAdmin && payState === "error" && (!pay || pay.pay.rateCardEngineer != null) && (
            <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {pay
                ? "The rate card check figure could not be refreshed just now, so the figure shown is the last one loaded. Reload the page."
                : "The rate card check figure could not be loaded just now. The hours below are unaffected."}
            </p>
          )}
          {isAdmin && pay && pay.pay.rateCardEngineer != null && (
            <TimesheetPayPanel report={pay} canDraftJournal={sheet.status === "APPROVED"} canEditBooking={sheet.canEdit} onChanged={() => void reload()} />
          )}

          {/* Session AVA: the weekly statement of hours (hours only) the subcontractor's invoice is written from. Office and
              admin alike: it carries no money, so the pay panel's admin-only gate does not apply to it. */}
          <TimesheetStatementPanel timesheetId={sheet.timesheetId} approved={sheet.status === "APPROVED"} />

          {(sheet.byType?.length ?? 0) > 0 && (
            <div className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <p className="mb-2 font-menu text-xs uppercase tracking-widest text-ele-blue">Where the hours went</p>
              <TimesheetBreakdown buckets={sheet.byType ?? []} />
            </div>
          )}

          {(sheet.unrecordedJobs?.length ?? 0) > 0 && (
            <UnrecordedJobsPanel
              jobs={sheet.unrecordedJobs ?? []}
              canEdit={sheet.canEdit}
              onAddTimes={(job) =>
                setFormState({
                  mode: "add",
                  date: job.date,
                  job: { jobId: job.jobId, label: [job.siteName, job.eleNumber].filter(Boolean).join(" | ") || jobTypeLabel(job.jobType) },
                })
              }
            />
          )}

          {sheet.days.every((d) => d.entries.length === 0) && (
            <p className="mb-4 rounded-ele border border-dashed border-ele-purple-accent p-4 text-sm text-ele-body">
              No timesheet entries for this week. Entries are added automatically when a job is approved, or can be added by
              hand below.
            </p>
          )}

          <div className="space-y-3">
            {sheet.days.map((day) => (
              <div key={day.date} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="font-heading text-sm uppercase tracking-wide text-ele-blue">{formatDateLong(day.date)}</h2>
                  <button
                    type="button"
                    onClick={() => setFormState({ mode: "add", date: day.date })}
                    disabled={!sheet.canEdit}
                    className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-40"
                  >
                    + Add entry
                  </button>
                </div>

                {day.entries.length === 0 ? (
                  <p className="text-sm text-ele-body opacity-70">No entries recorded.</p>
                ) : (
                  <ul className="space-y-2">
                    {day.entries.map((entry) => (
                      <li
                        key={entry.id}
                        className="flex flex-col gap-2 rounded-ele border border-ele-purple-accent/60 p-3 sm:flex-row sm:items-start sm:justify-between"
                      >
                        <div>
                          <p className="text-sm">
                            <span className={entry.minutes == null ? "text-[var(--ele-amber-text-deep)]" : "text-ele-strong"}>{entryTimeLabel(entry)}</span>
                            <span className="text-ele-strong"> | {entry.typeLabel}</span>
                            {entry.autoGenerated && <span className="ml-2 text-xs text-ele-body opacity-60">(auto)</span>}
                          </p>
                          {(entry.siteName || entry.eleNumber) && (
                            <p className="mt-0.5 text-xs text-ele-body">
                              {entry.siteName}
                              {entry.siteName && entry.eleNumber ? " · " : ""}
                              {entry.eleNumber && <span className="text-ele-blue">{entry.eleNumber}</span>}
                            </p>
                          )}
                          {entry.jobId && (
                            <p className="mt-0.5 text-xs text-ele-body">
                              Linked job:{" "}
                              {/* Same helper, same reason: a TimesheetEntry can carry a call-out's
                                  jobId, and this link predates the panel above. */}
                              <Link to={serviceRecordHref(entry.jobId, entry.jobType ?? "")} className="text-ele-green underline-offset-2 hover:underline">
                                View job
                              </Link>
                            </p>
                          )}
                          {entry.notes && <p className="mt-0.5 text-xs text-ele-body">{entry.notes}</p>}
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setFormState({ mode: "edit", entry })}
                            disabled={!sheet.canEdit}
                            className="max-sm:min-h-11 text-xs text-ele-blue hover:underline disabled:opacity-40"
                          >
                            Edit
                          </button>
                          <EntryDeleteButton
                            timesheetId={sheet.timesheetId}
                            entry={entry}
                            canEdit={sheet.canEdit}
                            onDeleted={() => void reload()}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 sm:grid-cols-4">
            <TotalField label="Travel" value={sheet.totals.travelMins} />
            <TotalField label="On site" value={sheet.totals.onSiteMins} />
            <TotalField label="Other" value={sheet.totals.otherMins} />
            <TotalField label="Total" value={sheet.totals.totalMins} emphasise />
          </div>

          {formState && weekStarting && (
            <EntryFormModal
              weekStarting={weekStarting}
              initialDate={formState.mode === "add" ? formState.date : formState.entry.date}
              entry={formState.mode === "edit" ? formState.entry : undefined}
              linkedJob={formState.mode === "add" ? formState.job : undefined}
              onClose={() => setFormState(null)}
              onSave={(payload) => (formState.mode === "add" ? saveEntry(payload) : saveEditedEntry(formState.entry.id, payload))}
              onSaved={() => {
                setFormState(null);
                void reload();
              }}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

// Completed visits whose hours reached no timesheet (Session ANF). Deliberately NOT folded into the
// day-by-day grid: these wrote no entry at all, so there is no row to sit on a day, and showing them
// as though there were would invite the office to read a zero where there is simply nothing.
//
// "ADD THE TIMES" IS THE WHOLE POINT OF THE PANEL, not a convenience. The list is derived live from
// "a completed visit with no TimesheetEntry against it", so an entry typed WITHOUT a job link never
// clears the row, however many hours it records. Nothing in the office UI had ever sent a jobId, so
// a panel without this button would have been a permanent, unclearable nag telling the office to do
// something that could not work. The button hands the form the job, and the saved entry clears the
// row it came from.
function UnrecordedJobsPanel({
  jobs,
  canEdit,
  onAddTimes,
}: {
  jobs: TimesheetUnrecordedJob[];
  canEdit: boolean;
  onAddTimes: (job: TimesheetUnrecordedJob) => void;
}) {
  return (
    <div className="mb-4 rounded-ele border border-ele-amber-line bg-ele-surface p-4">
      {/* --ele-amber-text-deep, never a literal: it is #b8860b on the office shell's DEFAULT DARK
          surface and #8a6508 on light. The literal light-ground ink measured 3.79:1 on bg-ele-surface,
          an AA failure on the very heading the feature exists to draw attention to. */}
      <p className="font-menu text-xs uppercase tracking-widest text-[var(--ele-amber-text-deep)]">Hours not on this timesheet</p>
      <p className="mt-1 text-sm text-ele-body">
        {jobs.length === 1 ? "This visit is" : "These visits are"} marked complete, but no usable arrival and departure was recorded against{" "}
        {jobs.length === 1 ? "it" : "them"}. Add the hours here, or correct the times on the job sheet.
      </p>
      <ul className="mt-3 space-y-2">
        {jobs.map((j) => (
          <li key={j.jobId} className="flex flex-col gap-2 rounded-ele border border-ele-purple-accent/60 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm text-ele-strong">
                {j.siteName ?? jobTypeLabel(j.jobType)}
                {j.eleNumber && <span className="text-ele-blue"> | {j.eleNumber}</span>}
              </p>
              <p className="mt-0.5 text-xs text-ele-body">
                {formatDate(j.date)} | {jobTypeLabel(j.jobType)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <button
                type="button"
                onClick={() => onAddTimes(j)}
                disabled={!canEdit}
                className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-40"
              >
                Add the times
              </button>
              {/* Through the shared helper, never a hardcoded path. A call-out's sheet lives on the
                  Call-outs page, not the Service Records page, and an unrecorded out-of-hours
                  call-out is the likeliest row in this panel: sending it to /office/jobsheets would
                  land the office on a page that lists no call-out and tells them "That job has not
                  been recorded yet", which is false and is the opposite of what this panel just
                  said. */}
              <Link to={serviceRecordHref(j.jobId, j.jobType)} className="text-xs text-ele-blue underline-offset-2 hover:underline">
                View job
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TotalField({ label, value, emphasise = false }: { label: string; value: number; emphasise?: boolean }) {
  return (
    <div>
      <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">{label}</p>
      <p className={`mt-1 text-lg ${emphasise ? "font-heading text-ele-green" : "text-ele-strong"}`}>{formatDurationHm(value)}</p>
    </div>
  );
}

// Two-step delete on the platform's standard branded pattern: the inline ConfirmButton IS the
// user-facing two-step (arm, then confirm), and the server's OWN two-step confirmation (a first
// call with no token returns one and deletes nothing) runs entirely inside the single onConfirm
// handler below, so from the office's point of view this is exactly one arm-then-confirm click
// sequence, not two. The confirm text is composed client-side from what the row already knows
// (the entry's type, date and length), so there is no need to wait on a server round trip just to
// show the office what is about to be deleted. A 409 confirmation_expired (the 60-second token
// lapsing) surfaces the server's own message; ConfirmButton's own reset means the next press
// simply restarts the chain from the top.
function EntryDeleteButton({
  timesheetId,
  entry,
  canEdit,
  onDeleted,
}: {
  timesheetId: string | null;
  entry: TimesheetEntry;
  canEdit: boolean;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function performDelete() {
    if (!timesheetId) return;
    setBusy(true);
    setError(null);
    try {
      const first = await api.timesheets.deleteEntry(timesheetId, entry.id);
      if (first && "confirmationToken" in first) {
        await api.timesheets.deleteEntry(timesheetId, entry.id, first.confirmationToken);
      }
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : "Could not delete that entry. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const summary = `${entry.typeLabel} entry on ${formatDate(entry.date)} (${formatDurationHm(entry.minutes)})`;

  return (
    <span className="flex flex-col items-end gap-1">
      <ConfirmButton
        onConfirm={() => void performDelete()}
        busy={busy}
        busyLabel="Deleting..."
        label="Delete"
        confirmLabel={`Delete this ${summary}?`}
        disabled={!canEdit || !timesheetId}
        className="max-sm:min-h-11 text-xs text-ele-danger hover:underline"
      />
      {error && (
        <span role="alert" className="text-xs text-ele-danger">
          {error}
        </span>
      )}
    </span>
  );
}

// Add or edit one entry. EITHER a start/end time pair OR a duration in minutes, never both; the
// choice is an explicit toggle rather than left for the office to infer, and it is freely
// reversible on edit in both directions:
//   - pair to duration: startTime/endTime are sent as explicit null (the only way to CLEAR them;
//     an omitted key leaves the stored value alone) alongside the new durationMins.
//   - duration to pair: startTime/endTime are sent and the server clears durationMins itself, so
//     it is never sent as null too.
// A conversion that would leave the entry with no length at all is rejected by the server (422);
// that message is surfaced rather than pre-empted client-side.
function EntryFormModal({
  weekStarting,
  initialDate,
  entry,
  linkedJob,
  onClose,
  onSave,
  onSaved,
}: {
  weekStarting: string;
  initialDate: string;
  entry?: TimesheetEntry;
  // Set when the office pressed "Add the times" on an unrecorded visit: the entry will be LINKED to
  // that job, which is what clears the row it came from. Shown rather than left implicit, because an
  // entry that quietly attaches itself to a job would be worse than one that does not attach at all.
  linkedJob?: { jobId: string; label: string };
  onClose: () => void;
  onSave: (payload: TimesheetEntryPatch & { confirmSecondAttendance?: boolean }) => Promise<void>;
  onSaved: () => void;
}) {
  const isEdit = Boolean(entry);
  const hasExistingPair = Boolean(entry?.startTime && entry?.endTime);
  const minDate = weekStarting;
  const maxDate = addDays(weekStarting, 6);

  const [date, setDate] = useState(entry?.date ?? initialDate);
  const [entryType, setEntryType] = useState<TimesheetEntryType>(entry?.entryType ?? "ON_SITE");
  const [mode, setMode] = useState<"range" | "duration">(hasExistingPair || !entry ? "range" : "duration");
  const [startTime, setStartTime] = useState(entry?.startTime ? toLocalTimeInput(entry.startTime) : "");
  const [endTime, setEndTime] = useState(entry?.endTime ? toLocalTimeInput(entry.endTime) : "");
  const [durationMins, setDurationMins] = useState(entry?.durationMins != null ? String(entry.durationMins) : "");
  const [notes, setNotes] = useState(entry?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // BDG2-R review finding 6 (Session BEM): the server's words when this job already has on-site time this week. The office either
  // corrects the entry already there or confirms, through the branded two-step, that the engineer really attended twice.
  const [duplicate, setDuplicate] = useState<string | null>(null);

  // The job this entry is linked to (Session ANX). `linkedJob` is the locked "Add the times" preset
  // above and is never editable here. Otherwise an edit starts from whatever the entry already
  // carries (same label recipe as the "Add the times" button, so the two never disagree on how a
  // job is named), and the office can search a different one or clear it via JobLinkField below.
  const [jobLink, setJobLink] = useState<LinkedJob | null>(
    linkedJob ??
      (entry?.jobId
        ? { jobId: entry.jobId, label: [entry.siteName, entry.eleNumber].filter(Boolean).join(" | ") || jobTypeLabel(entry.jobType ?? "") }
        : null),
  );

  async function handleSave(confirmSecondAttendance = false) {
    setError(null);
    setDuplicate(null);
    if (!date || date < minDate || date > maxDate) {
      setError("Choose a date within this week.");
      return;
    }
    let payload: TimesheetEntryPatch & { confirmSecondAttendance?: boolean };
    if (mode === "range") {
      if (!startTime || !endTime) {
        setError("Enter a start and end time.");
        return;
      }
      if (endTime <= startTime) {
        setError("The end time must be after the start time.");
        return;
      }
      // The server clears any stored durationMins itself once a start/end pair is set, so it is
      // never sent as null here as well.
      payload = { date, entryType, startTime: combineDateTime(date, startTime), endTime: combineDateTime(date, endTime) };
    } else {
      const mins = Number(durationMins);
      if (!Number.isFinite(mins) || mins <= 0) {
        setError("Enter a duration in minutes.");
        return;
      }
      payload = { date, entryType, durationMins: Math.round(mins) };
      // Converting an entry that had a recorded start/end pair to a duration must explicitly
      // clear the pair: the server leaves an omitted field untouched, only an explicit null
      // clears it.
      if (isEdit && hasExistingPair) {
        payload.startTime = null;
        payload.endTime = null;
      }
    }
    const trimmedNotes = notes.trim();
    if (trimmedNotes) payload.notes = trimmedNotes;

    // jobId follows the entry's own convention: omitted leaves it alone, an explicit null detaches
    // it. `linkedJob` always wins (the locked preset from "Add the times"); otherwise jobLink is
    // whatever the office picked, searched or cleared, and a clear on an entry that DID carry a job
    // is the one case that must send null rather than simply omitting the field.
    if (linkedJob) {
      payload.jobId = linkedJob.jobId;
    } else if (jobLink) {
      payload.jobId = jobLink.jobId;
    } else if (isEdit && entry?.jobId) {
      payload.jobId = null;
    }

    if (confirmSecondAttendance) payload.confirmSecondAttendance = true;

    setSaving(true);
    try {
      await onSave(payload);
      onSaved();
    } catch (err) {
      // Add and edit alike: the PATCH route refuses a re-link onto a job the week already holds on-site time on, so an edit
      // gets the same confirm-and-resend.
      if (err instanceof ApiError && err.status === 409 && err.body?.error === "duplicate_job_entry") {
        setDuplicate(err.message || "This week already has on-site time on this job.");
        return;
      }
      setError(err instanceof ApiError && err.message ? err.message : "Could not save that entry. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell
      ariaLabel={isEdit ? "Edit timesheet entry" : "Add timesheet entry"}
      onClose={() => !saving && onClose()}
      closeOnBackdrop
      width="max-w-lg"
      panelClassName="max-h-[90vh] overflow-y-auto p-6"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <h2 className="font-heading text-xl text-ele-blue">{isEdit ? "Edit entry" : "Add entry"}</h2>
        <button onClick={onClose} aria-label="Close" className="text-ele-body hover:text-ele-green">
          ✕
        </button>
      </div>

      {linkedJob ? (
        <p className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-inset px-3 py-2 text-sm text-ele-body">
          These hours will be recorded against <span className="text-ele-strong">{linkedJob.label}</span>.
        </p>
      ) : (
        <JobLinkField
          value={jobLink}
          disabled={saving}
          onChange={(job) => {
            setJobLink(job);
            // A job is site attendance whatever its type -- service, call-out, LOLER, repair,
            // condition report, site inspection or tech visit all put the engineer at the lift, and
            // none of them is a Travel/Office/Training/Other entry. Picking one is exactly the
            // moment the office is telling us that, so it is worth suggesting; they can still
            // change the Type select below for a genuine exception (e.g. correcting travel against
            // a job that was mostly on-site time).
            if (job) setEntryType("ON_SITE");
          }}
        />
      )}

      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm" htmlFor="tse-date">
            Date
          </label>
          <input
            id="tse-date"
            type="date"
            value={date}
            min={minDate}
            max={maxDate}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm" htmlFor="tse-type">
            Type
          </label>
          <select
            id="tse-type"
            value={entryType}
            onChange={(e) => setEntryType(e.target.value as TimesheetEntryType)}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
          >
            {ENTRY_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-3">
        <span className="mb-1 block text-sm">How was this time recorded?</span>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setMode("range")}
            disabled={saving}
            aria-pressed={mode === "range"}
            className={`max-sm:min-h-11 rounded-ele border px-3 py-1.5 text-xs tracking-wide ${
              mode === "range" ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body"
            }`}
          >
            Start and end time
          </button>
          <button
            type="button"
            onClick={() => setMode("duration")}
            disabled={saving}
            aria-pressed={mode === "duration"}
            className={`max-sm:min-h-11 rounded-ele border px-3 py-1.5 text-xs tracking-wide disabled:opacity-40 ${
              mode === "duration" ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body"
            }`}
          >
            Duration (minutes)
          </button>
        </div>
      </div>

      {mode === "range" ? (
        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm" htmlFor="tse-start">
              Start time
            </label>
            <input
              id="tse-start"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm" htmlFor="tse-end">
              End time
            </label>
            <input
              id="tse-end"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
            />
          </div>
        </div>
      ) : (
        <div className="mb-3">
          <label className="mb-1 block text-sm" htmlFor="tse-duration">
            Duration (minutes)
          </label>
          <input
            id="tse-duration"
            type="number"
            min={1}
            max={1440}
            value={durationMins}
            onChange={(e) => setDurationMins(e.target.value)}
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
          />
        </div>
      )}

      <label className="mb-1 block text-sm" htmlFor="tse-notes">
        Notes (optional)
      </label>
      <textarea
        id="tse-notes"
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
      />

      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      {duplicate && (
        <div role="alert" className="mb-3 rounded-ele border border-ele-amber-line bg-ele-amber-wash px-3 py-2 text-sm text-ele-amber-wash-ink">
          <p className="mb-2">{duplicate}</p>
          <ConfirmButton
            onConfirm={() => void handleSave(true)}
            busy={saving}
            busyLabel="Adding..."
            label="It was a second attendance: add it"
            confirmLabel="Add this as a second attendance on the job?"
            className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:bg-ele-inset"
          />
        </div>
      )}

      <div className={`gap-3 ${MODAL_ACTIONS}`}>
        <Button variant="ghost" className={MODAL_ACTION_BTN} onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button className={MODAL_ACTION_BTN} onClick={() => void handleSave()} disabled={saving}>
          {saving ? "Saving..." : "Save entry"}
        </Button>
      </div>
    </ModalShell>
  );
}

// A search/pick control for linking the entry to an existing job (Session ANX). Reuses the office's
// existing global record search (GET /api/search, already office/admin-gated and tenant-scoped) --
// its `jobs` arm matches a job reference and, since this session, the lift's site name too -- rather
// than a bespoke endpoint: POST/PATCH /:id/entries has taken and stored jobId since Session ANF,
// this control was simply the only thing missing.
const JOB_SEARCH_MIN_LENGTH = 2;
const JOB_SEARCH_DEBOUNCE_MS = 300;

function JobLinkField({
  value,
  onChange,
  disabled,
}: {
  value: LinkedJob | null;
  onChange: (job: LinkedJob | null) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const term = query.trim();
    if (term.length < JOB_SEARCH_MIN_LENGTH) {
      setResults([]);
      setSearchError(false);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    setSearchError(false);
    const t = setTimeout(() => {
      api
        .search(term, 6)
        .then((r) => {
          if (live) {
            setResults(r.jobs);
            setLoading(false);
          }
        })
        .catch(() => {
          if (live) {
            setResults([]);
            setSearchError(true);
            setLoading(false);
          }
        });
    }, JOB_SEARCH_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [query]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  if (value) {
    return (
      <div className="mb-3 flex items-center justify-between gap-2 rounded-ele border border-ele-purple-accent bg-ele-inset px-3 py-2 text-sm">
        <span className="min-w-0 truncate text-ele-body">
          Linked job: <span className="text-ele-strong">{value.label}</span>
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(null)}
          className="flex-none text-xs text-ele-blue hover:underline disabled:opacity-40"
        >
          Change
        </button>
      </div>
    );
  }

  const showDropdown = open && query.trim().length >= JOB_SEARCH_MIN_LENGTH;

  return (
    <div ref={containerRef} className="relative mb-3">
      <label className="mb-1 block text-sm" htmlFor="tse-job-search">
        Link a job (optional)
      </label>
      <input
        id="tse-job-search"
        type="text"
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search by reference or site..."
        autoComplete="off"
        className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
      />
      {showDropdown && (
        <div className="absolute z-10 mt-1 w-full rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] shadow-xl">
          {loading && <div className="px-3 py-2 text-xs text-ele-body opacity-70">Searching...</div>}
          {!loading && searchError && (
            <div role="alert" className="px-3 py-2 text-xs text-ele-danger">
              Could not search jobs. Please try again.
            </div>
          )}
          {!loading && !searchError && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-ele-body opacity-70">No jobs found.</div>
          )}
          {!loading && !searchError && results.length > 0 && (
            <ul>
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange({ jobId: r.id, label: r.subtitle ? `${r.label} · ${r.subtitle}` : r.label });
                      setQuery("");
                      setOpen(false);
                    }}
                    className="flex w-full items-baseline justify-between gap-3 px-3 py-1.5 text-left text-sm text-ele-strong hover:bg-ele-row-hover"
                  >
                    <span className="truncate">{r.label}</span>
                    {r.subtitle && <span className="flex-none truncate text-xs text-ele-body opacity-70">{r.subtitle}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
